import { useEffect, useState, useRef, ChangeEvent } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Product,
  ProductCategory,
  ProductOrigin,
  CATEGORY_LABELS,
  ORIGIN_LABELS,
  useCreateProduct,
  useUpdateProduct,
} from '@/hooks/warehouse';
import { uploadToCloudinary, getThumbnailImageUrl } from '@/lib/cloudinary';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  product?: Product | null;
}

const ProductFormModal = ({ isOpen, onClose, product }: ProductFormModalProps) => {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [origin, setOrigin] = useState<ProductOrigin>('otros');
  const [category, setCategory] = useState<ProductCategory>('limpieza');
  const [description, setDescription] = useState('');
  
  // Image states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: createProduct, isPending: isCreating } = useCreateProduct();
  const { mutate: updateProduct, isPending: isUpdating } = useUpdateProduct();

  useEffect(() => {
    if (product) {
      setName(product.name);
      setPrice(product.price.toString());
      setOrigin(product.origin);
      setCategory(product.category);
      setDescription(product.description || '');
      setExistingImageUrl(product.image_url);
    } else {
      setName('');
      setPrice('');
      setOrigin('otros');
      setCategory('limpieza');
      setDescription('');
      setExistingImageUrl(null);
    }
    // Reset file selection when modal opens/closes
    setSelectedFile(null);
    setImagePreview(null);
  }, [product, isOpen]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setExistingImageUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !price) return;

    setIsUploading(true);
    
    try {
      let imageUrl: string | null = existingImageUrl;
      
      // Upload new image if selected
      if (selectedFile) {
        imageUrl = await uploadToCloudinary(selectedFile);
      }

      const productData = {
        name: name.trim(),
        price: parseFloat(price),
        origin,
        category,
        description: description.trim() || null,
        image_url: imageUrl,
      };

      if (product) {
        updateProduct(
          { id: product.id, ...productData },
          { onSuccess: onClose }
        );
      } else {
        createProduct(productData, { onSuccess: onClose });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const isLoading = isCreating || isUpdating || isUploading;
  const currentImage = imagePreview || (existingImageUrl ? getThumbnailImageUrl(existingImageUrl) : null);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {product ? 'Editar Producto' : 'Nuevo Producto'}
            </DialogTitle>
            <DialogDescription>
              {product
                ? 'Modifica los datos del producto'
                : 'Completa los datos para crear un nuevo producto'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Image upload section */}
            <div className="grid gap-2">
              <Label>Imagen del producto (opcional)</Label>
              <div className="flex items-start gap-4">
                <div 
                  className="relative w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50 overflow-hidden cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {currentImage ? (
                    <img 
                      src={currentImage} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImagePlus className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Seleccionar
                  </Button>
                  {(currentImage) && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={handleRemoveImage}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Eliminar
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre del producto"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="price">Precio de referencia</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="category">Categoría</Label>
                <Select value={category} onValueChange={(v: ProductCategory) => setCategory(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona categoría" />
                  </SelectTrigger>
                <SelectContent>
                    {(Object.entries(CATEGORY_LABELS) as [ProductCategory, string][]).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="origin">Origen</Label>
                <Select value={origin} onValueChange={(v: ProductOrigin) => setOrigin(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona origen" />
                  </SelectTrigger>
                <SelectContent>
                    {(Object.entries(ORIGIN_LABELS) as [ProductOrigin, string][]).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Descripción (opcional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descripción del producto..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isUploading ? 'Subiendo imagen...' : isLoading ? 'Guardando...' : product ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductFormModal;
